
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api-client'
import type { FoodItem, CreateCustomFoodData } from '@/types/diet-v2'

// ============================================
// USDA FOOD DATABASE
// ============================================

// Search USDA foods
export function useSearchUSDAFoods(query: string, pageSize: number = 25) {
  return useQuery<{
    foods: Array<{
      fdcId: number
      description: string
      brandName: string | null
      dataType: string
    }>
    totalHits: number
    currentPage: number
    totalPages: number
  }>({
    queryKey: ['searchUSDAFoods', query, pageSize],
    queryFn: async () => {
      const response = await apiClient.get(`/food-library/usda/search?query=${encodeURIComponent(query)}&pageSize=${pageSize}`)
      return response.data
    },
    enabled: !!query && query.length >= 2,
  })
}

// Get USDA food details
export function useUSDAFoodDetails(fdcId: string | number) {
  return useQuery<{
    fdcId: number
    description: string
    calories: number
    protein: number
    carbs: number
    fat: number
    fiber: number
    servingSize: number
    servingUnit: string
  }>({
    queryKey: ['usdaFoodDetails', fdcId],
    queryFn: async () => {
      const response = await apiClient.get(`/food-library/usda/${fdcId}`)
      return response.data
    },
    enabled: !!fdcId,
  })
}

// ============================================
// CUSTOM FOODS
// ============================================

// Get all custom foods
export function useCustomFoods(category?: string, page: number = 1) {
  return useQuery<FoodItem[]>({
    queryKey: ['customFoods', category, page],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (category) params.append('category', category)
      params.append('page', String(page))
      
      const response = await apiClient.get(`/food-library/custom?${params.toString()}`)
      
      // Backend returns { foods: [...], total, page, pages }
      // Normalize to array
      if (response.data.foods) {
        return response.data.foods
      }
      
      // Handle both direct array response and wrapped response
      return Array.isArray(response.data) ? response.data : (response.data?.data || [])
    },
  })
}

// Search custom foods
export function useSearchCustomFoods(query: string) {
  return useQuery<FoodItem[]>({
    queryKey: ['searchCustomFoods', query],
    queryFn: async () => {
      const response = await apiClient.get(`/food-library/custom/search?query=${encodeURIComponent(query)}`)
      
      // Backend returns { foods: [...] }
      if (response.data.foods) {
        return response.data.foods
      }
      
      // Handle both direct array response and wrapped response
      return Array.isArray(response.data) ? response.data : (response.data?.data || [])
    },
    enabled: !!query && query.length >= 2,
  })
}

// Get custom food by ID
export function useCustomFood(id: string) {
  return useQuery<FoodItem>({
    queryKey: ['customFood', id],
    queryFn: async () => {
      const response = await apiClient.get(`/food-library/custom/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

// Create custom food
export function useCreateCustomFood() {
  const queryClient = useQueryClient()

  return useMutation<FoodItem, Error, CreateCustomFoodData>({
    mutationFn: async (data) => {
      const response = await apiClient.post('/food-library/custom', {
        name: data.name,
        category: data.category,
        calories: data.calories,
        macros: {
          protein: data.protein,
          carbs: data.carbs,
          fat: data.fat,
          fiber: data.fiber,
        },
        servingSize: data.servingSize,
        servingUnit: data.servingUnit,
      })
      return response.data
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Custom food created successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['customFoods'] })
      queryClient.invalidateQueries({ queryKey: ['searchCustomFoods'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create custom food',
        variant: 'destructive',
      })
    },
  })
}

// Update custom food
export function useUpdateCustomFood() {
  const queryClient = useQueryClient()

  return useMutation<FoodItem, Error, { id: string } & Partial<CreateCustomFoodData>>({
    mutationFn: async ({ id, ...data }) => {
      const payload: any = {
        name: data.name,
        category: data.category,
        calories: data.calories,
        servingSize: data.servingSize,
        servingUnit: data.servingUnit,
      }

      // Only include macros if any of the values are provided
      if (data.protein !== undefined || data.carbs !== undefined || data.fat !== undefined || data.fiber !== undefined) {
        payload.macros = {
          protein: data.protein,
          carbs: data.carbs,
          fat: data.fat,
          fiber: data.fiber,
        }
      }

      const response = await apiClient.patch(`/food-library/custom/${id}`, payload)
      return response.data
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Custom food updated successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['customFoods'] })
      queryClient.invalidateQueries({ queryKey: ['customFood'] })
      queryClient.invalidateQueries({ queryKey: ['searchCustomFoods'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update custom food',
        variant: 'destructive',
      })
    },
  })
}

// Delete custom food
export function useDeleteCustomFood() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/food-library/custom/${id}`)
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Custom food deleted successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['customFoods'] })
      queryClient.invalidateQueries({ queryKey: ['searchCustomFoods'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete custom food',
        variant: 'destructive',
      })
    },
  })
}

// ============================================
// COMBINED SEARCH (USDA + Custom)
// ============================================

// Search foods from both USDA and custom sources
export function useSearchFoods(query: string, source?: 'usda' | 'custom') {
  return useQuery<FoodItem[]>({
    queryKey: ['searchFoods', query, source],
    queryFn: async () => {
      if (!query || query.length < 2) return []

      const results: FoodItem[] = []

      // Add custom foods
      if (!source || source === 'custom') {
        try {
          const response = await apiClient.get(`/food-library/custom/search?query=${encodeURIComponent(query)}`)
          // Backend returns { foods: [...] }
          const customFoods = response.data.foods || (Array.isArray(response.data) ? response.data : (response.data?.data || []))
          results.push(...customFoods)
        } catch (error) {
          console.error('Error fetching custom foods:', error)
        }
      }

      // Add USDA foods
      if (!source || source === 'usda') {
        try {
          const response = await apiClient.get(`/food-library/usda/search?query=${encodeURIComponent(query)}&pageSize=10`)
          // Backend returns array directly
          const usdaResults = Array.isArray(response.data) ? response.data : (response.data?.data || [])
          
          const usdaFoods = usdaResults.map((food: any) => ({
            _id: food.fdcId || `usda_${food.fdcId}`,
            name: food.name || food.description || 'Unknown Food',
            source: 'usda' as const,
            fdcId: String(food.fdcId),
            servingSize: food.servingSize || 100,
            servingUnit: food.servingUnit || 'g',
            calories: food.calories || 0,
            macros: {
              protein: food.macros?.protein || 0,
              carbs: food.macros?.carbs || 0,
              fat: food.macros?.fat || 0,
              fiber: food.macros?.fiber || 0,
            },
          }))
          results.push(...usdaFoods)
        } catch (error) {
          console.error('Error fetching USDA foods:', error)
        }
      }

      return results
    },
    enabled: !!query && query.length >= 2,
  })
}

// Get food by ID (supports both USDA and custom)
export function useFood(id: string) {
  return useQuery<FoodItem | null>({
    queryKey: ['food', id],
    queryFn: async () => {
      // Check if it's a USDA food
      if (id.startsWith('usda_') || id.match(/^\d+$/)) {
        const fdcId = id.replace('usda_', '')
        const response = await apiClient.get(`/food-library/usda/${fdcId}`)
        const data = response.data
        
        return {
          _id: id,
          name: data.description,
          source: 'usda' as const,
          fdcId: String(data.fdcId),
          servingSize: data.servingSize,
          servingUnit: data.servingUnit,
          calories: data.calories,
          macros: {
            protein: data.protein,
            carbs: data.carbs,
            fat: data.fat,
            fiber: data.fiber,
          },
        }
      }
      
      // Otherwise it's a custom food
      const response = await apiClient.get(`/food-library/custom/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

// Get food by USDA FDC ID
export function useFoodByFdcId(fdcId: string) {
  return useUSDAFoodDetails(fdcId)
}

// Get recently used foods (placeholder - not in backend API spec)
export function useRecentFoods(userId: string, limit: number = 10) {
  return useQuery<FoodItem[]>({
    queryKey: ['recentFoods', userId, limit],
    queryFn: async () => {
      // This endpoint is not in the backend spec
      // Return empty array for now
      return []
    },
    enabled: !!userId,
  })
}

// Get popular foods (placeholder - not in backend API spec)
export function usePopularFoods(limit: number = 10) {
  return useQuery<FoodItem[]>({
    queryKey: ['popularFoods', limit],
    queryFn: async () => {
      // This endpoint is not in the backend spec
      // Return empty array for now
      return []
    },
  })
}
